import { useEffect } from "react";
import { loadSupabase } from "@/lib/supabaseLazy";
import { useAuth } from "@/contexts/AuthContext";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const migratedFlag = (userId: string) => `lesson-notes-migrated:${userId}`;

/**
 * One-time import of notes that were written before they moved into the
 * database. Runs once per user per browser, on first sign-in after the change.
 *
 * Nothing is removed from localStorage, and existing database rows always win:
 * the insert skips any lesson that already has a row, so a note typed on
 * another device can never be overwritten by a stale local copy.
 */
const LessonNotesSync = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const flag = migratedFlag(user.id);
    if (window.localStorage.getItem(flag)) return;

    const prefix = `lesson-notes:${user.id}:`;
    const pending: { lessonId: string; content: string }[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;
      const lessonId = key.slice(prefix.length);
      const content = window.localStorage.getItem(key);
      if (!UUID_RE.test(lessonId) || !content?.trim()) continue;
      pending.push({ lessonId, content });
    }

    if (pending.length === 0) {
      window.localStorage.setItem(flag, "1");
      return;
    }

    let cancelled = false;
    (async () => {
      const supabase = await loadSupabase();
      let failed = 0;
      // Row at a time: a note whose lesson has since been deleted would fail
      // the foreign key and take a whole batch down with it.
      for (const { lessonId, content } of pending) {
        const { error } = await supabase
          .from("lesson_notes")
          .upsert(
            { user_id: user.id, lesson_id: lessonId, body: content },
            { onConflict: "user_id,lesson_id", ignoreDuplicates: true }
          );
        if (error) failed++;
      }
      if (cancelled) return;
      // Only mark done if everything landed, so a transient failure retries
      // on the next sign-in rather than silently dropping notes.
      if (failed === 0) window.localStorage.setItem(flag, "1");
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return null;
};

export default LessonNotesSync;
